import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

const API_BASE = 'https://facturas.verdevalle.com.mx';

const EMPTY_FORM = {
  rfc_proveedor: '',
  tipo_documento: 'I',
  tipo_excepcion: 'forma_pago',
  codigo: '',
  activo: true
};

const DOC_LABELS = {
  I: 'Factura',
  E: 'Nota de credito',
  P: 'Complemento de pago'
};

const EXCEPTION_LABELS = {
  forma_pago: 'Forma de pago',
  metodo_pago: 'Metodo de pago'
};

function getErrorMessage(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback;
}

export default function PaymentExceptions() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const sortedRows = useMemo(() => rows || [], [rows]);

  const loadRows = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API_BASE}/api/payment-exceptions`, {
        withCredentials: true
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getErrorMessage(err, 'No se pudieron cargar las excepciones'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const payload = {
      ...form,
      rfc_proveedor: form.rfc_proveedor.trim().toUpperCase(),
      codigo: form.codigo.trim().toUpperCase()
    };

    if (!payload.rfc_proveedor || !payload.codigo) {
      setError('RFC proveedor y codigo son obligatorios');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_BASE}/api/payment-exceptions/${editingId}`, payload, {
          withCredentials: true
        });
        setSuccess('Excepcion actualizada');
      } else {
        await axios.post(`${API_BASE}/api/payment-exceptions`, payload, {
          withCredentials: true
        });
        setSuccess('Excepcion guardada');
      }
      resetForm();
      await loadRows();
    } catch (err) {
      setError(getErrorMessage(err, 'No se pudo guardar la excepcion'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (row) => {
    setEditingId(row.id);
    setForm({
      rfc_proveedor: row.rfc_proveedor || '',
      tipo_documento: row.tipo_documento || 'I',
      tipo_excepcion: row.tipo_excepcion || 'forma_pago',
      codigo: row.codigo || '',
      activo: Boolean(row.activo)
    });
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id) => {
    setError('');
    setSuccess('');
    try {
      await axios.delete(`${API_BASE}/api/payment-exceptions/${id}`, {
        withCredentials: true
      });
      setSuccess('Excepcion desactivada');
      await loadRows();
      if (editingId === id) resetForm();
    } catch (err) {
      setError(getErrorMessage(err, 'No se pudo desactivar la excepcion'));
    }
  };

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937' }}>
            Catalogo de excepciones de pago
          </Typography>

          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/')}
            startIcon={<ArrowBackIcon fontSize="small" />}
            sx={{ width: { xs: '100%', sm: 'auto' }, fontSize: '0.8rem' }}
          >
            Volver a factura
          </Button>
        </Box>

        {(error || success) && (
          <Alert severity={error ? 'error' : 'success'} onClose={() => { setError(''); setSuccess(''); }}>
            {error || success}
          </Alert>
        )}

        <Paper
          component="form"
          onSubmit={handleSubmit}
          sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              label="RFC proveedor"
              value={form.rfc_proveedor}
              onChange={(event) => updateForm('rfc_proveedor', event.target.value.toUpperCase())}
              inputProps={{ maxLength: 13 }}
              size="small"
              sx={{ minWidth: 180 }}
            />

            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Tipo documento</InputLabel>
              <Select
                label="Tipo documento"
                value={form.tipo_documento}
                onChange={(event) => updateForm('tipo_documento', event.target.value)}
              >
                <MenuItem value="I">Factura</MenuItem>
                <MenuItem value="E">Nota de credito</MenuItem>
                <MenuItem value="P">Complemento de pago</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Excepcion</InputLabel>
              <Select
                label="Excepcion"
                value={form.tipo_excepcion}
                onChange={(event) => updateForm('tipo_excepcion', event.target.value)}
              >
                <MenuItem value="forma_pago">Forma de pago</MenuItem>
                <MenuItem value="metodo_pago">Metodo de pago</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Codigo"
              value={form.codigo}
              onChange={(event) => updateForm('codigo', event.target.value.toUpperCase())}
              inputProps={{ maxLength: 10 }}
              size="small"
              sx={{ minWidth: 120 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={form.activo}
                  onChange={(event) => updateForm('activo', event.target.checked)}
                />
              }
              label="Activo"
              sx={{ whiteSpace: 'nowrap' }}
            />

            <Button
              type="submit"
              variant="contained"
              startIcon={editingId ? <SaveIcon /> : <AddCircleOutlineIcon />}
              disabled={saving}
              sx={{ minWidth: 130 }}
            >
              {editingId ? 'Actualizar' : 'Agregar'}
            </Button>

            {editingId && (
              <Button
                type="button"
                variant="outlined"
                startIcon={<CloseRoundedIcon />}
                onClick={resetForm}
              >
                Cancelar
              </Button>
            )}
          </Stack>
        </Paper>

        <TableContainer
          component={Paper}
          sx={{ borderRadius: 2, border: '1px solid #e5e7eb', overflowX: 'auto' }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: '#f3f4f6' } }}>
                <TableCell>RFC proveedor</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Codigo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Actualizado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : sortedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#6b7280' }}>
                    Sin excepciones registradas
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map(row => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ opacity: row.activo ? 1 : 0.58 }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{row.rfc_proveedor}</TableCell>
                    <TableCell>{DOC_LABELS[row.tipo_documento] || row.tipo_documento}</TableCell>
                    <TableCell>{EXCEPTION_LABELS[row.tipo_excepcion] || row.tipo_excepcion}</TableCell>
                    <TableCell>{row.codigo}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.activo ? 'Activo' : 'Inactivo'}
                        color={row.activo ? 'success' : 'default'}
                        variant={row.activo ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>{row.updated_at ? new Date(row.updated_at).toLocaleString() : '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEdit(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Desactivar">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.id)}
                            disabled={!row.activo}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Box>
  );
}
